import React from "react";
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  makeStyles,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
  description: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    borderRadius: 12,
    boxShadow: theme.palette.mode === "dark" 
      ? "0px 4px 20px rgba(0, 0, 0, 0.3)" 
      : "0px 4px 20px rgba(0, 0, 0, 0.06)",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: theme.palette.mode === "dark" 
      ? "rgba(255, 255, 255, 0.05)" 
      : "rgba(0, 0, 0, 0.02)",
  },
  headerCell: {
    fontWeight: 700,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
  },
  maskCode: {
    fontFamily: "monospace",
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: "0.9375rem",
    backgroundColor: theme.palette.mode === "dark" 
      ? "rgba(25, 118, 210, 0.15)" 
      : "rgba(25, 118, 210, 0.08)",
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    display: "inline-block",
  },
  descriptionCell: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  categoryChip: {
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  exampleBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === "dark" 
      ? "rgba(255, 255, 255, 0.05)" 
      : "rgba(0, 0, 0, 0.02)",
    borderRadius: 8,
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"}`,
  },
  exampleTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  exampleText: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    whiteSpace: "pre-wrap",
  },
}));

// Definição das máscaras disponíveis
const AVAILABLE_MASKS = [
  { code: "${queue.name}", description: "Nome da fila", category: "Fila", example: "Você foi transferido para a fila ${queue.name}" },
  { code: "${queue.id}", description: "ID da fila", category: "Fila", example: "Fila ID: ${queue.id}" },
  { code: "${user.name}", description: "Nome do usuário/atendente", category: "Usuário", example: "Atendido por ${user.name}" },
  { code: "${user.id}", description: "ID do usuário/atendente", category: "Usuário", example: "Atendente ID: ${user.id}" },
  { code: "${company.name}", description: "Nome da empresa", category: "Empresa", example: "Bem-vindo à ${company.name}" },
  { code: "${company.id}", description: "ID da empresa", category: "Empresa", example: "Empresa ID: ${company.id}" },
  { code: "${contact.name}", description: "Nome do contato", category: "Contato", example: "Olá ${contact.name}!" },
  { code: "${contact.number}", description: "Número do contato", category: "Contato", example: "Seu número: ${contact.number}" },
  { code: "${ticket.id}", description: "ID do ticket", category: "Ticket", example: "Ticket #${ticket.id}" },
  { code: "${ticket.status}", description: "Status do ticket", category: "Ticket", example: "Status: ${ticket.status}" },
];

const MaskReferenceGuide = () => {
  const classes = useStyles();

  // Agrupar máscaras por categoria
  const masksByCategory = AVAILABLE_MASKS.reduce((acc, mask) => {
    if (!acc[mask.category]) {
      acc[mask.category] = [];
    }
    acc[mask.category].push(mask);
    return acc;
  }, {});

  const categories = Object.keys(masksByCategory);

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>
        {i18n.t("settings.masks.title") || "Guia de Referência de Máscaras"}
      </Typography>
      <Typography className={classes.description}>
        {i18n.t("settings.masks.description") || 
          "Use máscaras dinâmicas para personalizar suas mensagens. Digite '@' nos campos de mensagem para ver as opções disponíveis."}
      </Typography>

      {categories.map((category) => (
        <Box key={category} style={{ marginBottom: 24 }}>
          <Typography 
            variant="h6" 
            style={{ 
              marginBottom: 12, 
              fontWeight: 600,
              color: "inherit",
            }}
          >
            {category}
          </Typography>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.headerCell}>
                    {i18n.t("settings.masks.mask") || "Máscara"}
                  </TableCell>
                  <TableCell className={classes.headerCell}>
                    {i18n.t("settings.masks.description") || "Descrição"}
                  </TableCell>
                  <TableCell className={classes.headerCell}>
                    {i18n.t("settings.masks.example") || "Exemplo"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {masksByCategory[category].map((mask, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography className={classes.maskCode}>
                        {mask.code}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.descriptionCell}>
                      {mask.description}
                    </TableCell>
                    <TableCell className={classes.descriptionCell}>
                      <Typography 
                        component="span" 
                        style={{ 
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                        }}
                      >
                        {mask.example}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      <Box className={classes.exampleBox}>
        <Typography className={classes.exampleTitle}>
          {i18n.t("settings.masks.howToUse") || "Como usar:"}
        </Typography>
        <Typography className={classes.exampleText}>
          {i18n.t("settings.masks.usageInstructions") || 
            `1. Ao editar uma mensagem, digite "@" para abrir o menu de máscaras
2. Digite parte do nome da máscara ou descrição para filtrar
3. Clique na máscara desejada ou pressione Enter para selecionar
4. A máscara será inserida automaticamente no texto

Exemplo de mensagem completa:
"Olá \${contact.name}! Você foi transferido para a fila \${queue.name} e será atendido por \${user.name}."`}
        </Typography>
      </Box>
    </Box>
  );
};

export default MaskReferenceGuide;
